import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowLeft, ArrowRight, Save, Trash2, RefreshCw, Lock, AlertTriangle, Calculator } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import StatusBadge from "@/components/masterOrder/StatusBadge";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  listOrders, listClients, listOrderItems, listProductTypes,
  listRecipes, listPackagingItems, createCostingDraft, getCosting,
  updateCosting, recalcCosting, finalizeCosting, nextDraftCosting,
  listCostingVersions, getCostingCurrent, COSTING_STATUS_LABELS,
  costingStatusTone, SCALING_METHODS, mapCostingError,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const err = (e) => toast.error(mapCostingError(e) || formatApiError(e));
const fmt = (v) => (v == null || v === "" ? "—" : v);
const round2 = (v) => {
  if (v == null || v === "") return v;
  const n = Number(v);
  return Number.isFinite(n) ? (Math.round(n * 100) / 100).toString() : v;
};

/* =============================== LIST =================================== */
export function MOCostings() {
  const nav = useNavigate();
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [pts, setPts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      setLoading(true);
      try {
        const [os, cs, ps] = await Promise.all([listOrders(), listClients(), listProductTypes()]);
        setOrders(os); setClients(cs); setPts(ps);
        // Collect items + costings versions per order (fan-out)
        const allItems = (await Promise.all(os.map((o) => listOrderItems(o.id)))).flat();
        setItems(allItems);
        const allVers = (await Promise.all(allItems.map((it) => listCostingVersions(it.id)))).flat();
        setRows(allVers);
      } catch (e) { err(e); } finally { setLoading(false); }
    })();
  }, [aLoad, mode]);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const ptMap = Object.fromEntries(pts.map((p) => [p.id, p]));
  const orderMap = Object.fromEntries(orders.map((o) => [o.id, o]));
  const itemMap = Object.fromEntries(items.map((it) => [it.id, it]));

  const filtered = useMemo(() => {
    let out = rows.slice();
    if (statusFilter) out = out.filter((r) => r.status === statusFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((r) => {
        const it = itemMap[r.order_item_id];
        const o = orderMap[r.order_id];
        const cli = o ? clientMap[o.client_id] : null;
        return (
          String(o?.order_number || "").toLowerCase().includes(s) ||
          (cli?.name || "").toLowerCase().includes(s) ||
          (ptMap[it?.product_type_id]?.name || "").toLowerCase().includes(s)
        );
      });
    }
    out.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return out;
  }, [rows, q, statusFilter, clientMap, itemMap, orderMap, ptMap]);

  return (
    <MOShell active="costing" title="Калькуляции" subtitle="Расчёты по позициям заказов">
      {aLoad ? <LoadingBlock /> : mode === "no_access" || mode === "demo" ? <NoAccessScreen /> : (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Поиск: № заказа, клиент, тип продукта..."
                   className="flex-1 h-10 px-4 rounded-full text-[13px] outline-none bg-white"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-costings-search" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                    data-testid="mo-costings-status">
              <option value="">Все статусы</option>
              {Object.entries(COSTING_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {canWrite && (
              <button onClick={() => nav("/master-order/costings/new")}
                      className="h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-costings-new">
                <Plus size={15} strokeWidth={1.8} /> Новая калькуляция
              </button>
            )}
          </div>
          {loading ? <LoadingBlock /> : filtered.length === 0 ? (
            <div className="rounded-xl bg-white p-10 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-costings-empty">
              <Calculator className="mx-auto" size={22} strokeWidth={1.5} color="#8B6F4B" />
              <div className="mt-3 text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                Калькуляций пока нет
              </div>
              <p className="text-[12.5px] mt-1.5" style={{ color: "#8B6F4B" }}>
                Создайте расчёт для позиции заказа — здесь появится история версий.
              </p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden bg-white" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <table className="w-full text-[12.5px]" data-testid="mo-costings-table">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">Заказ</th>
                    <th className="text-left px-3 py-2 font-normal">Клиент</th>
                    <th className="text-left px-3 py-2 font-normal">Позиция</th>
                    <th className="text-left px-3 py-2 font-normal">Ver.</th>
                    <th className="text-left px-3 py-2 font-normal">Статус</th>
                    <th className="text-right px-3 py-2 font-normal">Себестоимость</th>
                    <th className="text-right px-3 py-2 font-normal">Продажа</th>
                    <th className="text-right px-3 py-2 font-normal">Прибыль</th>
                    <th className="text-left px-3 py-2 font-normal">Обновлён</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const o = orderMap[r.order_id];
                    const it = itemMap[r.order_item_id];
                    const cli = o ? clientMap[o.client_id] : null;
                    return (
                      <tr key={r.id} className="border-t hover:bg-[#FBF5EA] cursor-pointer" style={{ borderColor: "#EFE4D0" }}
                          onClick={() => nav(`/master-order/costings/${r.id}`)}
                          data-testid={`mo-costings-row-${r.id}`}>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#8B6F4B" }}>№ {o?.order_number || "—"}</td>
                        <td className="px-3 py-2" style={{ color: "#2A1D14" }}>{cli?.name || "—"}</td>
                        <td className="px-3 py-2" style={{ color: "#4A3728" }}>{ptMap[it?.product_type_id]?.name || "—"}</td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#4A3728" }}>
                          v{r.version ?? "—"}{r.is_current ? " • тек." : ""}
                          {r.needs_recalculation && (<span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#F5D6C2", color: "#8A3F1E" }}>пересч.</span>)}
                        </td>
                        <td className="px-3 py-2"><StatusBadge tone={costingStatusTone(r.status)}>{COSTING_STATUS_LABELS[r.status]}</StatusBadge></td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#2A1D14" }}>{fmt(r.totals?.total_cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#2A1D14" }}>{fmt(r.sale_price)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#2A1D14" }}>{fmt(r.totals?.profit)}</td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#8B6F4B" }}>{r.updated_at?.slice(0,10) || "—"}</td>
                        <td className="px-3 py-2 text-right"><ArrowRight size={13} color="#8B6F4B" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </MOShell>
  );
}

/* =============================== NEW ==================================== */
export function MOCostingNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialItemId = sp.get("orderItemId");
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [pts, setPts] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [itemId, setItemId] = useState(initialItemId || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      const [os, ps] = await Promise.all([listOrders(), listProductTypes()]);
      setOrders(os); setPts(ps);
      // load items for all orders
      const its = (await Promise.all(os.map((o) => listOrderItems(o.id)))).flat();
      setItems(its);
      if (initialItemId) {
        const it = its.find((i) => i.id === initialItemId);
        if (it) setOrderId(it.order_id);
      }
    })();
  }, [aLoad, mode, initialItemId]);

  const orderItems = items.filter((it) => it.order_id === orderId);

  const proceed = async () => {
    if (!itemId) return toast.error("Выберите позицию заказа");
    setBusy(true);
    try {
      // If a costing already exists for this OrderItem, jump to current instead of creating duplicate
      const current = await getCostingCurrent(itemId);
      if (current && current.status === "draft") {
        return nav(`/master-order/costings/${current.id}`);
      }
      if (current && current.status === "finalized") {
        // Offer to create next-draft via studio (nav to current — user will click "Новая версия")
        return nav(`/master-order/costings/${current.id}`);
      }
      const draft = await createCostingDraft({
        order_id: orderId, order_item_id: itemId,
        recipe_components: [], packaging_lines_input: [], labor_lines_input: [], other_cost_lines: [],
        tech_loss_percent: "0",
      });
      toast.success("Черновик создан");
      nav(`/master-order/costings/${draft.id}`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <MOShell active="costing" title="Новая калькуляция" subtitle="Выбор позиции заказа">
      {aLoad ? <LoadingBlock /> : mode === "no_access" || mode === "demo" ? <NoAccessScreen /> :
       !canWrite ? <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>Режим просмотра — создание недоступно.</div> :
       (
        <div className="max-w-xl space-y-4">
          <button onClick={() => nav("/master-order/costings")} className="text-[12.5px] inline-flex items-center gap-1.5" style={{ color: "#8B6F4B" }}>
            <ArrowLeft size={13} /> К списку калькуляций
          </button>
          <div className="rounded-xl bg-white p-5 space-y-3" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            {orders.length === 0 ? (
              <div className="text-center py-6" data-testid="mo-costing-new-no-orders">
                <div className="text-[15px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Нет доступных заказов
                </div>
                <p className="text-[12.5px] mt-1" style={{ color: "#8B6F4B" }}>
                  Создайте заказ и добавьте в него позицию — тогда можно будет рассчитать её себестоимость.
                </p>
                <button onClick={() => nav("/master-order/orders/new")}
                        className="mt-4 h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                        style={{ background: "#4A3728", color: "#F7EFE0" }}
                        data-testid="mo-costing-new-create-order">
                  <Plus size={13} /> Создать заказ
                </button>
              </div>
            ) : (<>
            <Field label="Заказ *">
              <select value={orderId} onChange={(e) => { setOrderId(e.target.value); setItemId(""); }}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-costing-new-order">
                <option value="">— выбрать —</option>
                {orders.map((o) => <option key={o.id} value={o.id}>№ {displayOrderNumber(o.order_number)} · {o.event_date}</option>)}
              </select>
            </Field>
            <Field label="Позиция заказа *">
              <select value={itemId} onChange={(e) => setItemId(e.target.value)}
                      disabled={!orderId}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white disabled:opacity-50"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-costing-new-item">
                <option value="">— выбрать —</option>
                {orderItems.map((it) => {
                  const pt = pts.find((p) => p.id === it.product_type_id);
                  return <option key={it.id} value={it.id}>{pt?.name || "Позиция"} · {it.quantity} шт.</option>;
                })}
              </select>
              {orderId && orderItems.length === 0 && (
                <div className="mt-2 rounded-lg p-2.5 text-[12px]"
                     style={{ background: "#FBF5EA", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                     data-testid="mo-costing-new-no-items">
                  У этого заказа ещё нет позиций.{" "}
                  <button type="button" onClick={() => nav(`/master-order/orders/${orderId}`)}
                          className="underline" style={{ color: "#4A3728" }}
                          data-testid="mo-costing-new-add-items-link">
                    Открыть заказ и добавить позицию
                  </button>
                </div>
              )}
            </Field>
            <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
              Если у позиции уже есть открытый черновик — откроется он.
              Если есть финализированная версия — она откроется; следующую версию можно создать из редактора.
            </div>
            </>)}
            {orders.length > 0 && (
              <div className="flex justify-end">
                <button onClick={proceed} disabled={busy || !itemId}
                        className="h-10 px-5 rounded-full text-[13px] disabled:opacity-60"
                        style={{ background: "#4A3728", color: "#F7EFE0" }}
                        data-testid="mo-costing-new-go">
                  {busy ? "..." : "Продолжить"}
                </button>
              </div>
            )}
          </div>
        </div>
       )}
    </MOShell>
  );
}

/* ============================== STUDIO ================================== */
export function MOCostingStudio() {
  const { costingId } = useParams();
  const nav = useNavigate();
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [orderItem, setOrderItem] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [packagingItems, setPackagingItems] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);

  // Editable draft-only state
  const [recipeComp, setRecipeComp] = useState({ recipe_id: "", scaling_method: "quantity", target_quantity: "1", target_weight: "", target_weight_unit: "g", manual_scale_factor: "1" });
  const [packagingLines, setPackagingLines] = useState([]);
  const [laborLines, setLaborLines] = useState([]);
  const [otherLines, setOtherLines] = useState([]);
  const [techLoss, setTechLoss] = useState("0");
  const [salePrice, setSalePrice] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const cur = await getCosting(costingId);
      setC(cur);
      // Load order + item
      const [orders] = await Promise.all([listOrders({})]);
      const o = orders.find((x) => x.id === cur.order_id);
      setOrder(o);
      if (o) {
        const its = await listOrderItems(o.id);
        setOrderItem(its.find((i) => i.id === cur.order_item_id));
      }
      const [rc, pk, pts, vs] = await Promise.all([listRecipes(), listPackagingItems(), listProductTypes(), listCostingVersions(cur.order_item_id)]);
      setRecipes(rc); setPackagingItems(pk); setProductTypes(pts);
      // Backend /versions endpoint returns finalized/superseded only. Merge in the
      // current record and (if different) the current draft so the sidebar shows
      // the full history for this order-item.
      const merged = [...vs];
      const addUnique = (item) => { if (item && !merged.some((v) => v.id === item.id)) merged.push(item); };
      addUnique(cur);
      try {
        const currentDraft = await getCostingCurrent(cur.order_item_id);
        addUnique(currentDraft);
      } catch (_) { /* ignore — versions still usable without draft */ }
      merged.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
      setVersions(merged);
      // Seed editable state from current
      const rcInput = (cur.recipe_components || [])[0];
      if (rcInput) setRecipeComp({
        recipe_id: rcInput.recipe_id || "",
        scaling_method: rcInput.scaling_method || "quantity",
        target_quantity: rcInput.target_quantity != null ? String(rcInput.target_quantity) : "1",
        target_weight: rcInput.target_weight != null ? String(rcInput.target_weight) : "",
        target_weight_unit: rcInput.target_weight_unit || "g",
        manual_scale_factor: rcInput.manual_scale_factor != null ? String(rcInput.manual_scale_factor) : "1",
      });
      setPackagingLines(cur.packaging_lines_input || []);
      setLaborLines(cur.labor_lines_input || []);
      setOtherLines(cur.other_cost_lines || []);
      setTechLoss(String(cur.tech_loss_percent ?? "0"));
      setSalePrice(cur.sale_price != null ? String(cur.sale_price) : "");
    } catch (e) { err(e); } finally { setLoading(false); }
  };
  useEffect(() => { if (!aLoad && mode !== "no_access" && mode !== "demo") load(); /* eslint-disable-line */ }, [costingId, aLoad, mode]);

  const buildRC = () => {
    if (!recipeComp.recipe_id) return [];
    const rc = { recipe_id: recipeComp.recipe_id, scaling_method: recipeComp.scaling_method };
    if (recipeComp.scaling_method === "quantity") rc.target_quantity = recipeComp.target_quantity || "1";
    if (recipeComp.scaling_method === "weight")   { rc.target_weight = recipeComp.target_weight || "0"; rc.target_weight_unit = recipeComp.target_weight_unit || "g"; }
    if (recipeComp.scaling_method === "manual")   rc.manual_scale_factor = recipeComp.manual_scale_factor || "1";
    return [rc];
  };

  const savePatch = async () => {
    setBusy(true);
    try {
      const updated = await updateCosting(costingId, {
        recipe_components: buildRC(),
        packaging_lines_input: packagingLines.filter((l) => l.packaging_item_id && l.quantity),
        labor_lines_input: laborLines.filter((l) => l.hours && l.hourly_rate),
        other_cost_lines: otherLines.filter((l) => l.description && l.total_cost),
        tech_loss_percent: techLoss || "0",
        sale_price: salePrice || null,
      });
      setC(updated); setVersions((v) => v.map((x) => x.id === updated.id ? updated : x));
      toast.success("Сохранено и пересчитано");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const doRecalc = async () => {
    setBusy(true);
    try { const u = await recalcCosting(costingId); setC(u); toast.success("Пересчитано"); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const doFinalize = async () => {
    setBusy(true); setConfirmFinalize(false);
    try {
      const u = await finalizeCosting(costingId); setC(u);
      const vs = await listCostingVersions(u.order_item_id);
      const merged = vs.some((v) => v.id === u.id) ? vs : [u, ...vs];
      merged.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
      setVersions(merged);
      toast.success(`Версия ${u.version} финализирована`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const doNextDraft = async () => {
    setBusy(true);
    try { const u = await nextDraftCosting(costingId); toast.success(`Черновик v${(u.version ?? "") } создан`); nav(`/master-order/costings/${u.id}`); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  if (aLoad || loading) return <MOShell active="costing" title="Калькуляция"><LoadingBlock /></MOShell>;
  if (mode === "no_access" || mode === "demo") return <MOShell active="costing" title="Калькуляция"><NoAccessScreen /></MOShell>;
  if (!c) return <MOShell active="costing" title="Калькуляция"><div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>Калькуляция не найдена</div></MOShell>;

  const readOnly = !canWrite || c.status !== "draft";
  const cur = c.currency_code;
  const t = c.totals || {};
  const pt = productTypes.find((p) => p.id === orderItem?.product_type_id);

  return (
    <MOShell active="costing" title={`Калькуляция v${c.version ?? "черновик"}`} subtitle={order ? `№ ${displayOrderNumber(order.order_number)}` : ""}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/master-order/costings")} className="text-[12.5px] inline-flex items-center gap-1.5" style={{ color: "#8B6F4B" }}>
              <ArrowLeft size={13} /> К калькуляциям
            </button>
            {order && (
              <>
                <span className="text-[11px]" style={{ color: "#B9A692" }}>·</span>
                <button onClick={() => nav(`/master-order/orders/${order.id}`)}
                        className="text-[12.5px] inline-flex items-center gap-1.5 hover:underline"
                        style={{ color: "#8B6F4B" }}
                        data-testid="mo-costing-back-to-order">
                  К заказу № {displayOrderNumber(order.order_number)}
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge tone={costingStatusTone(c.status)}>{COSTING_STATUS_LABELS[c.status]}</StatusBadge>
            {c.is_current && <span className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: "#DDE6D3", color: "#3E5236" }}>текущая</span>}
          </div>
        </div>

        {/* Linked order context */}
        <section className="rounded-xl p-4" style={{ background: "linear-gradient(90deg, #F5EBD8, #FBF5EA)", boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-costing-context">
          <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Позиция заказа</div>
          <div className="text-[16px] mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            № {displayOrderNumber(order?.order_number)} · {order?.event_date}{order?.event_time ? ` ${order.event_time.slice(0,5)}` : ""} · {pt?.name || "—"}
            {orderItem?.quantity && <span className="text-[13px] opacity-70"> · {orderItem.quantity} шт.</span>}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "#8B6F4B" }}>Валюта расчёта = валюта заказа: <b>{cur}</b>. FX не применяется.</div>
        </section>

        {c.needs_recalculation && (
          <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: "#F5D6C2", color: "#8A3F1E" }} data-testid="mo-needs-recalc">
            <AlertTriangle size={16} />
            <div className="flex-1 text-[13px]">Данные заказа изменились. Калькуляцию нужно пересчитать.</div>
            {c.status === "draft" && canWrite && (
              <button onClick={doRecalc} disabled={busy}
                      className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
                      style={{ background: "#8A3F1E", color: "#FBF5EA" }}
                      data-testid="mo-recalc-btn">
                <RefreshCw size={12} /> Пересчитать
              </button>
            )}
          </div>
        )}

        {(c.missing_prices || []).length > 0 && (
          <div className="rounded-xl p-3 text-[12.5px]" style={{ background: "#F5EBD8", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #DCC6A0" }} data-testid="mo-missing-prices">
            Нет подходящей исторической цены: {c.missing_prices.map((m) => m.name).join(", ")}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4">
          {/* LEFT: form / lines */}
          <div className="space-y-4">
            <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Рецепт и масштабирование</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Рецепт">
                  <select value={recipeComp.recipe_id} onChange={(e) => setRecipeComp({ ...recipeComp, recipe_id: e.target.value })}
                          disabled={readOnly}
                          className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white disabled:opacity-60"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-recipe">
                    <option value="">— не выбран —</option>
                    {recipes.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.yield_quantity} {r.yield_unit})</option>)}
                  </select>
                </Field>
                <Field label="Метод масштабирования">
                  <select value={recipeComp.scaling_method} onChange={(e) => setRecipeComp({ ...recipeComp, scaling_method: e.target.value })}
                          disabled={readOnly}
                          className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white disabled:opacity-60"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-scaling-method">
                    {SCALING_METHODS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                {recipeComp.scaling_method === "quantity" && (
                  <Field label="Целевое количество"><Input value={recipeComp.target_quantity} onChange={(v) => setRecipeComp({ ...recipeComp, target_quantity: v })} testid="mo-target-qty" /></Field>
                )}
                {recipeComp.scaling_method === "weight" && (<>
                  <Field label="Целевая масса"><Input value={recipeComp.target_weight} onChange={(v) => setRecipeComp({ ...recipeComp, target_weight: v })} testid="mo-target-weight" /></Field>
                  <Field label="Единица массы">
                    <select value={recipeComp.target_weight_unit} onChange={(e) => setRecipeComp({ ...recipeComp, target_weight_unit: e.target.value })}
                            className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                            data-testid="mo-target-weight-unit">
                      <option value="g">g</option><option value="kg">kg</option>
                    </select>
                  </Field>
                </>)}
                {recipeComp.scaling_method === "manual" && (
                  <Field label="Коэффициент"><Input value={recipeComp.manual_scale_factor} onChange={(v) => setRecipeComp({ ...recipeComp, manual_scale_factor: v })} testid="mo-manual-factor" /></Field>
                )}
                <Field label="Технологические потери, %"><Input value={techLoss} onChange={setTechLoss} testid="mo-tech-loss" /></Field>
              </div>
              <div className="text-[11px] mt-2" style={{ color: "#8B6F4B" }}>Технологические потери применяются только к стоимости ингредиентов.</div>
            </section>

            <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-ingredient-lines">
              <div className="text-[17px] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Ингредиенты (расчёт сервера)</div>
              {(c.ingredient_lines || []).length === 0 ? (
                <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Ингредиенты появятся после выбора рецепта и сохранения.</div>
              ) : (
                <div className="rounded-lg overflow-hidden" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                  <table className="w-full text-[12px]">
                    <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                      <tr><th className="text-left px-2 py-1.5 font-normal">Ингредиент</th><th className="text-right px-2 py-1.5 font-normal">Норм. кол-во</th><th className="text-right px-2 py-1.5 font-normal">Ед. цена</th><th className="text-right px-2 py-1.5 font-normal">Сумма</th></tr>
                    </thead>
                    <tbody>
                      {c.ingredient_lines.map((l, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: "#EFE4D0" }}>
                          <td className="px-2 py-1.5" style={{ color: "#2A1D14" }}>{l.name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: "#4A3728" }}>{l.normalized_quantity} {l.base_unit}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: "#4A3728" }}>{l.unit_cost_snapshot}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: "#2A1D14" }}>{l.line_cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-packaging">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Упаковка</div>
                {!readOnly && (
                  <button onClick={() => setPackagingLines([...packagingLines, { packaging_item_id: "", quantity: "1", unit: "pcs" }])}
                          className="h-8 px-3 rounded-full text-[12px]" style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728" }}>
                    <Plus size={12} className="inline mr-1" />Строка
                  </button>
                )}
              </div>
              {packagingLines.length === 0 && <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Строк упаковки нет.</div>}
              {packagingLines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_60px_28px] gap-2 mb-2">
                  <select value={l.packaging_item_id} disabled={readOnly}
                          onChange={(e) => setPackagingLines(packagingLines.map((x, k) => k === i ? { ...x, packaging_item_id: e.target.value } : x))}
                          className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                    <option value="">—</option>
                    {packagingItems.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input value={l.quantity} disabled={readOnly}
                         onChange={(e) => setPackagingLines(packagingLines.map((x, k) => k === i ? { ...x, quantity: e.target.value } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  <select value={l.unit || "pcs"} disabled={readOnly}
                          onChange={(e) => setPackagingLines(packagingLines.map((x, k) => k === i ? { ...x, unit: e.target.value } : x))}
                          className="h-9 px-1 rounded text-[12px] bg-white disabled:opacity-60"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                    {["pcs","cm","m","g","kg","ml","l"].map((u) => <option key={u}>{u}</option>)}
                  </select>
                  {!readOnly && (
                    <button onClick={() => setPackagingLines(packagingLines.filter((_, k) => k !== i))}
                            className="w-9 h-9 grid place-items-center rounded" style={{ color: "#8A3F1E" }}><Trash2 size={12} /></button>
                  )}
                </div>
              ))}
            </section>

            <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-labor">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Работа</div>
                {!readOnly && (
                  <button onClick={() => setLaborLines([...laborLines, { description: "", hours: "1", hourly_rate: "0" }])}
                          className="h-8 px-3 rounded-full text-[12px]" style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728" }}>
                    <Plus size={12} className="inline mr-1" />Строка
                  </button>
                )}
              </div>
              {laborLines.length === 0 && <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Строк нет.</div>}
              {laborLines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_80px_28px] gap-2 mb-2">
                  <input value={l.description || ""} disabled={readOnly} placeholder="Описание"
                         onChange={(e) => setLaborLines(laborLines.map((x, k) => k === i ? { ...x, description: e.target.value } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  <input value={l.hours} disabled={readOnly} placeholder="ч"
                         onChange={(e) => setLaborLines(laborLines.map((x, k) => k === i ? { ...x, hours: e.target.value } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  <input value={l.hourly_rate} disabled={readOnly} placeholder={`ставка ${cur}`}
                         onChange={(e) => setLaborLines(laborLines.map((x, k) => k === i ? { ...x, hourly_rate: e.target.value } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  {!readOnly && <button onClick={() => setLaborLines(laborLines.filter((_, k) => k !== i))}
                                       className="w-9 h-9 grid place-items-center rounded" style={{ color: "#8A3F1E" }}><Trash2 size={12} /></button>}
                </div>
              ))}
            </section>

            <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-other">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Прочие расходы</div>
                {!readOnly && (
                  <button onClick={() => setOtherLines([...otherLines, { description: "", total_cost: "0", currency_code: cur }])}
                          className="h-8 px-3 rounded-full text-[12px]" style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728" }}>
                    <Plus size={12} className="inline mr-1" />Строка
                  </button>
                )}
              </div>
              {otherLines.length === 0 && <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Строк нет.</div>}
              {otherLines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_28px] gap-2 mb-2">
                  <input value={l.description || ""} disabled={readOnly} placeholder="Описание"
                         onChange={(e) => setOtherLines(otherLines.map((x, k) => k === i ? { ...x, description: e.target.value } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  <input value={l.total_cost} disabled={readOnly} placeholder={cur}
                         onChange={(e) => setOtherLines(otherLines.map((x, k) => k === i ? { ...x, total_cost: e.target.value, currency_code: cur } : x))}
                         className="h-9 px-2 rounded text-[12px] bg-white disabled:opacity-60" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  {!readOnly && <button onClick={() => setOtherLines(otherLines.filter((_, k) => k !== i))}
                                       className="w-9 h-9 grid place-items-center rounded" style={{ color: "#8A3F1E" }}><Trash2 size={12} /></button>}
                </div>
              ))}
            </section>
          </div>

          {/* RIGHT: sticky summary + version history */}
          <aside className="space-y-4">
            <div className="rounded-xl bg-white p-5 sticky top-4" style={{ boxShadow: "0 8px 22px -14px rgba(58,43,31,0.35), inset 0 0 0 1px #EFE4D0" }} data-testid="mo-costing-summary">
              <div className="text-[17px] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Итоги</div>
              <div className="space-y-1 text-[12.5px]" style={{ color: "#4A3728" }}>
                <Row k="Себестоимость ингредиентов" v={t.ingredient_cost} cur={cur} />
                <Row k="Технологические потери" v={t.tech_loss_cost} cur={cur} />
                <Row k="Упаковка" v={t.packaging_cost} cur={cur} />
                <Row k="Работа" v={t.labor_cost} cur={cur} />
                <Row k="Прочие расходы" v={t.other_cost} cur={cur} />
                <div className="border-t my-2" style={{ borderColor: "#EFE4D0" }} />
                <Row k="Полная себестоимость" v={t.total_cost} cur={cur} bold />
                <div className="border-t my-2" style={{ borderColor: "#EFE4D0" }} />
                <Field label={`Цена продажи (${cur})`}>
                  <Input value={salePrice} onChange={setSalePrice} testid="mo-sale-price" disabled={readOnly} />
                </Field>
                <Row k="Прибыль" v={t.profit} cur={cur} />
                <Row k="Маржа, %" v={round2(t.margin_percent)} />
                <Row k="Наценка, %" v={round2(t.markup_percent)} />
              </div>

              <div className="mt-3 space-y-2">
                {c.status === "draft" && canWrite && (<>
                  <button onClick={savePatch} disabled={busy}
                          className="w-full h-10 rounded-full text-[13px] inline-flex items-center justify-center gap-1.5"
                          style={{ background: "#4A3728", color: "#F7EFE0" }}
                          data-testid="mo-save">
                    <Save size={14} /> Сохранить и пересчитать
                  </button>
                  <button onClick={() => setConfirmFinalize(true)} disabled={busy}
                          className="w-full h-10 rounded-full text-[13px] inline-flex items-center justify-center gap-1.5"
                          style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                          data-testid="mo-finalize">
                    <Lock size={14} /> Финализировать
                  </button>
                </>)}
                {c.status === "finalized" && canWrite && (
                  <button onClick={doNextDraft} disabled={busy}
                          className="w-full h-10 rounded-full text-[13px]"
                          style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-next-draft">
                    Создать следующую версию
                  </button>
                )}
                {c.status !== "draft" && (
                  <div className="text-[11.5px] text-center" style={{ color: "#8B6F4B" }}>
                    Только чтение · v{c.version} · {COSTING_STATUS_LABELS[c.status]}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-white p-4" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-versions">
              <div className="text-[15px] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Версии</div>
              <div className="space-y-1.5">
                {versions.map((v) => (
                  <button key={v.id} onClick={() => nav(`/master-order/costings/${v.id}`)}
                          className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-black/5 ${v.id === c.id ? "bg-[#F5EBD8]" : ""}`}
                          data-testid={`mo-version-${v.id}`}>
                    <span className="text-[11px] tabular-nums w-9 shrink-0" style={{ color: "#8B6F4B" }}>v{v.version ?? "—"}</span>
                    <StatusBadge tone={costingStatusTone(v.status)}>{COSTING_STATUS_LABELS[v.status]}</StatusBadge>
                    <span className="flex-1 text-right text-[11px] tabular-nums" style={{ color: "#4A3728" }}>{fmt(v.totals?.total_cost)} {cur}</span>
                    {v.is_current && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#DDE6D3", color: "#3E5236" }}>тек.</span>}
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {confirmFinalize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(35,23,16,0.42)" }} onClick={() => setConfirmFinalize(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}
               style={{ boxShadow: "0 30px 60px -20px rgba(35,23,16,0.4)" }} data-testid="mo-finalize-confirm">
            <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Финализировать калькуляцию?</div>
            <p className="text-[13px] mt-2" style={{ color: "#4A3728" }}>
              После финализации эта версия станет снимком расчёта и не будет редактироваться.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmFinalize(false)} className="h-10 px-4 rounded-full text-[12.5px]"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>Отменить</button>
              <button onClick={doFinalize} disabled={busy} className="h-10 px-5 rounded-full text-[12.5px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-finalize-confirm-yes">
                Финализировать
              </button>
            </div>
          </div>
        </div>
      )}
    </MOShell>
  );
}

function Row({ k, v, cur, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "#5B4326", fontWeight: bold ? 500 : 400 }}>{k}</span>
      <span className="tabular-nums" style={{ color: "#2A1D14", fontWeight: bold ? 600 : 400 }}>
        {v == null || v === "" ? "—" : `${v}${cur ? ` ${cur}` : ""}`}
      </span>
    </div>
  );
}
